'use client';

import { useEffect } from 'react';

let lockCount = 0;
let lockedScrollY = 0;
let previousScrollBehavior = '';
let removeRootScrollGuards: (() => void) | null = null;

const ROOT_SCROLL_KEYS = new Set([
  ' ',
  'PageUp',
  'PageDown',
  'End',
  'Home',
  'ArrowUp',
  'ArrowDown',
]);

function installRootScrollGuards(): () => void {
  const onScroll = () => {
    if (window.scrollY !== lockedScrollY) {
      window.scrollTo({ top: lockedScrollY, behavior: 'auto' });
    }
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (!ROOT_SCROLL_KEYS.has(event.key)) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (!target) {
      event.preventDefault();
      return;
    }
    const tagName = target.tagName;
    const isTypingContext =
      target.isContentEditable ||
      tagName === 'INPUT' ||
      tagName === 'TEXTAREA' ||
      tagName === 'SELECT';
    if (!isTypingContext) {
      event.preventDefault();
    }
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('keydown', onKeyDown);

  return () => {
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('keydown', onKeyDown);
  };
}

export function useScrollLock(isLocked: boolean): void {
  useEffect(() => {
    if (!isLocked) {
      return;
    }

    if (lockCount === 0) {
      lockedScrollY = window.scrollY;
      previousScrollBehavior = document.documentElement.style.scrollBehavior;
      // Avoid smooth-scroll animation when forcing scroll back to locked position.
      document.documentElement.style.scrollBehavior = 'auto';
      removeRootScrollGuards = installRootScrollGuards();
    }

    lockCount += 1;

    return () => {
      lockCount = Math.max(0, lockCount - 1);

      if (lockCount === 0) {
        if (removeRootScrollGuards) {
          removeRootScrollGuards();
          removeRootScrollGuards = null;
        }
        document.documentElement.style.scrollBehavior = previousScrollBehavior;
        window.scrollTo(0, lockedScrollY);
      }
    };
  }, [isLocked]);
}
